<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../../stores/authStore';
import unionIcon from '../../assets/union.svg';
import loginBg from '../../assets/login-bg.jpg';

const { t } = useI18n();

// Branded landing / sign-in screen. The auth guard routes unauthenticated
// users here (rather than bouncing straight to the IdP) and authStore.logout()
// lands here too (logout is local-only — it doesn't end the IdP session).
// A click on "Sign in" kicks off the OIDC redirect via authStore.login(),
// requesting the IdP account picker since the IdP session is still alive.

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const signingIn = ref(false);

// The guard hands us the originally-requested path as ?returnTo= so we can
// deep-link the user back there after the OIDC round-trip. login() validates
// and stashes it; an unsafe/missing value is simply dropped.
const returnTo = computed(() => {
    const raw = route.query.returnTo;
    return typeof raw === 'string' ? raw : undefined;
});

function signIn(): void {
    signingIn.value = true;
    // Explicit sign-in → ask the IdP for its account picker. We keep the
    // IdP session alive across logout, so without this the user would be
    // silently single-signed-on with no chance to choose an account.
    authStore.login(returnTo.value, { selectAccount: true });
}

onMounted(() => {
    // A signed-in user has no business on the splash (e.g. hit Back after
    // login, or typed /login). Send them where they were headed, else home.
    if (authStore.isAuthenticated) {
        if (returnTo.value) {
            void router.replace(returnTo.value);
        } else {
            void router.replace({ name: 'matter-selection' });
        }
    }
});
</script>

<template>
    <div class="login-splash" :style="{ backgroundImage: `url(${loginBg})` }">
        <div class="login-splash-content">
            <img :src="unionIcon" class="login-splash-logo" alt="" />
            <p class="login-splash-heading">{{ t('auth.loginHeading') }}</p>
            <button
                type="button"
                class="login-splash-button"
                :disabled="signingIn"
                @click="signIn"
            >
                <svg
                    class="login-splash-button-icon"
                    viewBox="0 0 21 21"
                    aria-hidden="true"
                    focusable="false"
                >
                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                <span>{{
                    signingIn
                        ? t('auth.redirecting')
                        : t('auth.continueWithMicrosoft')
                }}</span>
            </button>
        </div>
    </div>
</template>

<style scoped>
.login-splash {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2rem;
    background-color: #f2f2f2;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

.login-splash-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;
    max-width: 420px;
    width: 100%;
    /* The mockup centers logo/subtitle/two-buttons as a block; with only one
       button we shift the shorter block up so it sits above dead-center. */
    margin-bottom: 12vh;
}

.login-splash-logo {
    width: 56px;
    height: 56px;
}

.login-splash-heading {
    margin: 0 0 8px;
    color: var(--color-text-primary);
    font-size: var(--text-lg, 1.125rem);
    font-weight: var(--font-weight-semibold);
}

.login-splash-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 14px 28px;
    background: var(--color-secondary);
    color: var(--color-bg-white);
    border: none;
    border-radius: var(--radius-pill);
    font-size: var(--text-lg, 1rem);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
}

.login-splash-button-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
}

.login-splash-button:hover:not(:disabled),
.login-splash-button:focus-visible:not(:disabled) {
    background: #253345;
    outline: none;
}

.login-splash-button:disabled {
    opacity: 0.7;
    cursor: progress;
}
</style>
